import type {
  ExecutiveMemoActionItemType,
  ExecutiveMemoDetailData,
  ExecutiveMemoFailedStage,
  ExecutiveMemoJobStatusData,
  ExecutiveMemoPriority,
  ExecutiveMemoUpdatePayload,
} from "./api";
import { AppError } from "./errors";
import {
  clearPendingJobNotifications,
  consumePendingJobNotification,
  readPendingJobNotification,
  type ExecutiveMemoJobNotification,
  writePendingJobNotification,
} from "./jobNotifications";

export const EXECUTIVE_MEMO_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const EXECUTIVE_MEMO_PENDING_JOB_STORAGE_KEY = "wizmate.executive-memo.pending-job.v1";
// 複数写真化（10_decisions/executive-memo-multi-photo-design.md 17章）。実際の上限はbackend側config
// （executive_memo.max_pages_per_memo）が権威を持つ。ここはUI側の早期フィードバック用の目安値。
export const EXECUTIVE_MEMO_MAX_PAGES = 10;

export const EXECUTIVE_MEMO_ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;

const EXECUTIVE_MEMO_ACCEPTED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];
const EXECUTIVE_MEMO_BODY_REQUIRED_MESSAGE =
  "画像から文字を読み取れませんでした。文字がはっきり写った画像で、もう一度お試しください。";
const EXECUTIVE_MEMO_FAILURE_FALLBACK_MESSAGE =
  "社長メモの処理に失敗しました。画像を確認して、もう一度お試しください。";
const EXECUTIVE_MEMO_INTERNAL_STAGE_LABELS = [
  "画像検証",
  "エージェント連携",
  "文字起こし",
  "整理・解析",
  "保存",
  "処理",
];

export const EXECUTIVE_MEMO_KIND_OPTIONS = ["アイデア", "商談", "タスク", "資金繰り", "契約", "採用", "メモ"] as const;

const KIND_LABEL_MAP: Record<string, string> = {
  strategy: "アイデア",
  sales: "商談",
  finance: "資金繰り",
  people: "採用",
  operations: "タスク",
  other: "メモ",
};

export function formatExecutiveMemoKindLabel(kind?: string | null): string {
  if (!kind) return "";
  return KIND_LABEL_MAP[kind] ?? kind;
}
export const EXECUTIVE_MEMO_THEME_OPTIONS = ["相談", "調査", "意思決定", "会議", "顧客", "採用"] as const;
export const EXECUTIVE_MEMO_PRIORITY_OPTIONS: ExecutiveMemoPriority[] = ["high", "mid", "low"];

export type PendingExecutiveMemoJobSnapshot = {
  jobId: string;
  clientRequestId: string;
  startedAt: string;
};

export type PendingExecutiveMemoJobNotification = Omit<ExecutiveMemoJobNotification, "kind">;

function hasWindow() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalize(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function containsJapaneseText(value: string) {
  return /[\u3040-\u30ff\u3400-\u9fff]/.test(value);
}

function looksLikeInternalEnglishError(value: string) {
  return /[a-z]/i.test(value) && !containsJapaneseText(value);
}

function stripExecutiveMemoInternalStageLabel(value: string) {
  const labels = EXECUTIVE_MEMO_INTERNAL_STAGE_LABELS.join("|");
  return value
    .replace(new RegExp(`\\s*[（(](?:${labels})[）)]\\s*$`), "")
    .trim();
}

export function formatExecutiveMemoFailureMessage(value?: string | null) {
  const message = normalize(value);
  if (!message) return EXECUTIVE_MEMO_FAILURE_FALLBACK_MESSAGE;

  const displayMessage = stripExecutiveMemoInternalStageLabel(message);
  const normalizedMessage = displayMessage.toLowerCase().replace(/\s+/g, " ").trim();
  if (
    normalizedMessage === "executive memo body is required." ||
    normalizedMessage === "executive memo body is required" ||
    normalizedMessage.includes("executive memo body is required")
  ) {
    return EXECUTIVE_MEMO_BODY_REQUIRED_MESSAGE;
  }

  if (containsJapaneseText(displayMessage)) return displayMessage;
  if (looksLikeInternalEnglishError(displayMessage)) return EXECUTIVE_MEMO_FAILURE_FALLBACK_MESSAGE;
  return displayMessage;
}

function compactStringArray(values: string[]) {
  return values.map((value) => value.trim()).filter((value) => value.length > 0);
}

function normalizeActionItemType(value: string): ExecutiveMemoActionItemType {
  if (value === "check" || value === "consult") return value;
  return "todo";
}

function toStoredExecutiveMemoJobNotification(
  notification: PendingExecutiveMemoJobNotification
): ExecutiveMemoJobNotification {
  return {
    kind: "executive_memo",
    jobId: notification.jobId,
    status: notification.status,
    processedAt: notification.processedAt,
    memoId: notification.memoId,
    title: notification.title,
    summary: notification.summary,
    failedStage: notification.failedStage,
    failedError: notification.failedError
      ? formatExecutiveMemoFailureMessage(notification.failedError)
      : notification.failedError,
  };
}

function fromStoredExecutiveMemoJobNotification(
  notification: ExecutiveMemoJobNotification | null
): PendingExecutiveMemoJobNotification | null {
  if (!notification) return null;

  return {
    jobId: notification.jobId,
    status: notification.status,
    processedAt: notification.processedAt,
    memoId: notification.memoId,
    title: notification.title,
    summary: notification.summary,
    failedStage: notification.failedStage,
    failedError: notification.failedError
      ? formatExecutiveMemoFailureMessage(notification.failedError)
      : notification.failedError,
  };
}

export function createExecutiveMemoClientRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `exec-memo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getExecutiveMemoPollIntervalMs(elapsedMs: number) {
  if (elapsedMs < 10_000) return 2_000;
  if (elapsedMs < 30_000) return 5_000;
  if (elapsedMs < 60_000) return 10_000;
  return 15_000;
}

export function isAcceptedExecutiveMemoImageType(file: File) {
  if (EXECUTIVE_MEMO_ACCEPTED_IMAGE_TYPES.includes(
    file.type as (typeof EXECUTIVE_MEMO_ACCEPTED_IMAGE_TYPES)[number]
  )) {
    return true;
  }

  const loweredName = file.name.toLowerCase();
  return EXECUTIVE_MEMO_ACCEPTED_IMAGE_EXTENSIONS.some((extension) => loweredName.endsWith(extension));
}

export function getExecutiveMemoFileValidationMessage(file: File) {
  if (!isAcceptedExecutiveMemoImageType(file)) {
    return "JPEG / PNG / WEBP / HEIC / HEIF の画像を選択してください。";
  }

  if (file.size > EXECUTIVE_MEMO_MAX_FILE_SIZE_BYTES) {
    return "社長メモ画像は10MB以下にしてください。";
  }

  return null;
}

// receipt.ts の compressReceiptImage と同じキャンバス処理（OCR前処理の圧縮方針を両機能で揃える）。
// 高解像度スマホ写真のOOM対策として選択時にここで縮小してからアップロードする。
// HEIC/HEIFはブラウザの<img>でデコードできずimg.onerrorになるため、元ファイルのまま返す
// （その場合は原寸のままアップロードされる。receipt側と同じ既知の制約）。
export function compressExecutiveMemoImage(
  file: File,
  maxLongSide = 1200,
  quality = 0.75
): Promise<File> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const scale = Math.min(1, maxLongSide / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const compressedName = file.name.replace(/\.[^.]+$/, ".jpg");
          resolve(new File([blob], compressedName, { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality
      );
    };

    img.src = objectUrl;
  });
}

export function readPendingExecutiveMemoJobSnapshot(): PendingExecutiveMemoJobSnapshot | null {
  if (!hasWindow()) return null;

  const raw = window.localStorage.getItem(EXECUTIVE_MEMO_PENDING_JOB_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const jobId = normalize(parsed?.jobId);
    const clientRequestId = normalize(parsed?.clientRequestId);
    const startedAt = normalize(parsed?.startedAt);
    if (!jobId || !clientRequestId || !startedAt) return null;

    return { jobId, clientRequestId, startedAt };
  } catch {
    return null;
  }
}

export function writePendingExecutiveMemoJobSnapshot(snapshot: PendingExecutiveMemoJobSnapshot) {
  if (!hasWindow()) return;

  window.localStorage.setItem(
    EXECUTIVE_MEMO_PENDING_JOB_STORAGE_KEY,
    JSON.stringify({
      jobId: snapshot.jobId,
      clientRequestId: snapshot.clientRequestId,
      startedAt: snapshot.startedAt,
    })
  );
}

export function clearPendingExecutiveMemoJobSnapshot() {
  if (!hasWindow()) return;
  window.localStorage.removeItem(EXECUTIVE_MEMO_PENDING_JOB_STORAGE_KEY);
}

export function readPendingExecutiveMemoJobNotification() {
  return fromStoredExecutiveMemoJobNotification(
    readPendingJobNotification("executive_memo") as ExecutiveMemoJobNotification | null
  );
}

export function writePendingExecutiveMemoJobNotification(
  notification: PendingExecutiveMemoJobNotification
) {
  // 同種の通知は1件のジョブしか同時に追跡しないため、processing書き込み時も
  // 必ず既存の同種通知をクリアしてから書き込む（そうしないと過去の未dismiss通知が残存し、
  // 新しいジョブの通知と同時に表示されてしまう）
  clearPendingJobNotifications("executive_memo");
  writePendingJobNotification(toStoredExecutiveMemoJobNotification(notification));
}

export function clearPendingExecutiveMemoJobNotification() {
  clearPendingJobNotifications("executive_memo");
}

export function consumePendingExecutiveMemoJobNotification() {
  return fromStoredExecutiveMemoJobNotification(
    consumePendingJobNotification("executive_memo") as ExecutiveMemoJobNotification | null
  );
}

export function toPendingExecutiveMemoJobNotification(
  response: ExecutiveMemoJobStatusData
): PendingExecutiveMemoJobNotification | null {
  if (
    response.relayStatus !== "completed" &&
    response.relayStatus !== "failed" &&
    response.relayStatus !== "timed_out"
  ) {
    return null;
  }

  return {
    jobId: response.jobId,
    status: response.relayStatus,
    processedAt: response.processedAt ?? undefined,
    memoId: response.memoId ?? undefined,
    failedStage: response.failedStage ?? undefined,
    failedError: response.failedError ? formatExecutiveMemoFailureMessage(response.failedError) : undefined,
  };
}

export function formatExecutiveMemoStageLabel(stage?: ExecutiveMemoFailedStage | string | null) {
  switch ((stage ?? "").trim().toLowerCase()) {
    case "validate":
      return "画像検証";
    case "relay":
      return "エージェント連携";
    case "ocr":
      return "文字起こし";
    case "structure":
      return "整理・解析";
    case "persist":
      return "保存";
    default:
      return "処理";
  }
}

export function formatExecutiveMemoPriorityLabel(priority?: ExecutiveMemoPriority | string | null) {
  switch (priority) {
    case "high":
      return "高";
    case "mid":
      return "中";
    case "low":
      return "低";
    default:
      return "未設定";
  }
}

export function formatExecutiveMemoDateTime(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function toDateTimeLocalValue(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function getCurrentDateTimeLocalValue(date = new Date()) {
  return toDateTimeLocalValue(date.toISOString());
}

export function dateTimeLocalToIso(value: string) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

export function parseCommaSeparatedValues(value: string) {
  return compactStringArray(value.split(","));
}

export function buildCommaSeparatedValue(values: string[]) {
  return compactStringArray(values).join(", ");
}

export function getBackendMessage(error: unknown, fieldNames: string[] = []) {
  if (!(error instanceof AppError)) return undefined;

  const responseText = error.meta.responseText?.trim();
  if (!responseText) return undefined;

  try {
    const parsed = JSON.parse(responseText);

    if (typeof parsed?.message === "string" && parsed.message.trim()) {
      return parsed.message.trim();
    }

    if (typeof parsed?.detail?.message === "string" && parsed.detail.message.trim()) {
      return parsed.detail.message.trim();
    }

    for (const fieldName of fieldNames) {
      const fieldErrors = parsed?.errors?.[fieldName];
      if (Array.isArray(fieldErrors) && typeof fieldErrors[0] === "string") {
        return fieldErrors[0];
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function buildExecutiveMemoUpdatePayload(
  input: {
    title?: string;
    body?: string;
    kind?: string;
    themes?: string[];
    tags?: string[];
    priority?: ExecutiveMemoPriority;
    actionItems?: { type: string; text: string }[];
    relatedAgents?: string[];
    status?: "active" | "archived";
  },
  version: number
): ExecutiveMemoUpdatePayload {
  return {
    version,
    title: normalize(input.title),
    body: normalize(input.body),
    kind: normalize(input.kind),
    themes: input.themes ? compactStringArray(input.themes) : undefined,
    tags: input.tags ? compactStringArray(input.tags) : undefined,
    priority: input.priority,
    actionItems: input.actionItems
      ?.map((item) => ({
        type: normalizeActionItemType(item.type),
        text: normalize(item.text) ?? "",
      }))
      .filter((item) => item.text.length > 0),
    relatedAgents: input.relatedAgents ? compactStringArray(input.relatedAgents) : undefined,
    status: input.status,
  };
}

// 読み取り成功したページのみギャラリー表示の対象にする（design 9.3章: 除外ページは事後表示のみ）。
export function isExecutiveMemoPageReadable(status: string) {
  return status !== "excluded" && status !== "ocr_failed" && status !== "upload_failed";
}

export function createExecutiveMemoDetailDraft(memo: ExecutiveMemoDetailData) {
  return {
    title: memo.title ?? "",
    body: memo.body,
    kind: memo.kind ?? "other",
    themes: memo.themes,
    tags: memo.tags,
    priority: memo.priority,
    actionItems: memo.actionItems.map((item) => ({ ...item, uid: createExecutiveMemoClientRequestId() })),
    relatedAgents: memo.relatedAgents,
    status: memo.status,
  };
}
